import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, filter } from 'rxjs';
import { selectActiveTenant } from '../../../auth/store/auth.selectors';
import { AdminUserPreferenceScope, SINGLETON_PREFERENCE_KEY } from '../../api/types/admin-user-preference';
import { AdminUserTenant } from '../../api/types/GraphQL';
import { LAST_TENANT_PREFERENCE, toLastTenantPreference } from '../../utils/tenant-storage.util';
import { PreferencesService } from './preferences.service';

const GLOBAL = AdminUserPreferenceScope.Global;

/**
 * NBO-2517: the tenant the user last worked in lives on the account (`lastTenant`), so their next
 * login opens in it on any machine. It is READ by the setTenants reducer from the login answer;
 * this adapter WRITES it — the tenant that is active once the account's rows are in.
 *
 * Writing it from the active tenant, rather than at each switch, covers every way a tenant becomes
 * active: a switch (the top bar or Settings write the session key and reload; the reloaded app
 * writes the row), the first login, and the self-heal when the stored tenant was revoked. Never
 * before the rows have loaded: the tenant resolved then may be this machine's guess, and a `set`
 * would beat the row another machine wrote.
 *
 * Constructed by AppInitializationService.
 */
@Injectable({ providedIn: 'root' })
export class LastTenantPreferenceService {
  private readonly preferences = inject(PreferencesService);

  constructor() {
    combineLatest([
      inject(Store)
        .select(selectActiveTenant)
        .pipe(filter((tenant): tenant is AdminUserTenant => !!tenant)),
      this.preferences.whenLoaded(GLOBAL)
    ]).subscribe(([tenant]) => {
      // combineLatest keeps the previous login's "loaded" — a new user's tenant can arrive before
      // their rows do. Equal values are no-ops in the service, so re-emissions send nothing.
      if (!this.preferences.isLoaded(GLOBAL)) return;
      this.preferences.set(LAST_TENANT_PREFERENCE, SINGLETON_PREFERENCE_KEY, GLOBAL, toLastTenantPreference(tenant.id));
    });
  }
}
